import axios from 'axios';
import { FlightDataTransformer } from '../transformers/FlightDataTransformer';

/**
 * AmadeusProvider - Wrapper for Amadeus Flight Offers Search API
 * Provides normalized flight search using Amadeus APIs
 */
export class AmadeusProvider {
    constructor() {
        this.transformer = new FlightDataTransformer();
        this.baseUrl = 'https://api.amadeus.com';
        this.tokenUrl = 'https://api.amadeus.com/v1/security/oauth2/token';
        this.searchUrl = 'https://api.amadeus.com/v2/shopping/flight-offers';

        this.apiKey = process.env.REACT_APP_AMADEUS_API_KEY;
        this.apiSecret = process.env.REACT_APP_AMADEUS_API_SECRET;

        this.accessToken = null;
        this.tokenExpiry = null;

        // Validate API credentials
        if (!this.apiKey || !this.apiSecret) {
            console.warn('AmadeusProvider: API credentials not found in environment variables');
        }
    }

    /**
     * Authenticate with Amadeus API and get access token
     * @returns {Promise<string>} Access token
     */
    async authenticate() {
        // Return existing token if still valid
        if (this.accessToken && this.tokenExpiry > Date.now()) {
            return this.accessToken;
        }

        if (!this.apiKey || !this.apiSecret) {
            throw new Error('Amadeus API credentials not configured. Please set REACT_APP_AMADEUS_API_KEY and REACT_APP_AMADEUS_API_SECRET environment variables.');
        }

        console.log('AmadeusProvider: Authenticating with Amadeus API...');
        console.log('AmadeusProvider: Using API Key:', this.apiKey ? `${this.apiKey.substring(0, 8)}...` : 'undefined');
        console.log('AmadeusProvider: Using API Secret:', this.apiSecret ? `${this.apiSecret.substring(0, 8)}...` : 'undefined');

        try {
            // Create form data for Amadeus authentication
            const formData = new URLSearchParams();
            formData.append('grant_type', 'client_credentials');
            formData.append('client_id', this.apiKey);
            formData.append('client_secret', this.apiSecret);

            const response = await axios.post(this.tokenUrl, formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                timeout: 10000
            });

            this.accessToken = response.data.access_token;
            // Set expiry 5 minutes before actual expiry for safety
            this.tokenExpiry = Date.now() + ((response.data.expires_in - 300) * 1000);

            console.log('AmadeusProvider: Authentication successful');
            console.log('AmadeusProvider: Access token received:', this.accessToken ? `${this.accessToken.substring(0, 20)}...` : 'undefined');
            console.log('AmadeusProvider: Token expires in:', response.data.expires_in, 'seconds');
            return this.accessToken;

        } catch (error) {
            console.error('AmadeusProvider: Authentication failed', error);

            if (error.response) {
                const { status, data } = error.response;
                if (status === 401) {
                    throw new Error('Amadeus authentication failed: Invalid API credentials');
                } else if (status === 429) {
                    throw new Error('Amadeus authentication failed: Rate limit exceeded');
                } else {
                    throw new Error(`Amadeus authentication failed: ${status} ${data.error_description || data.error || 'Unknown error'}`);
                }
            } else if (error.code === 'ECONNABORTED') {
                throw new Error('Amadeus authentication timed out. Please try again.');
            } else {
                throw new Error(`Amadeus authentication error: ${error.message}`);
            }
        }
    }

    /**
     * Search for flights using Amadeus API
     * @param {Object} params - Search parameters
     * @param {string} params.origin - Origin airport code
     * @param {string} params.destination - Destination airport code
     * @param {string} params.departureDate - Departure date (YYYY-MM-DD)
     * @param {string} params.returnDate - Return date (YYYY-MM-DD)
     * @param {number} params.adults - Number of adult passengers
     * @param {string} params.currency - Currency code (e.g., "CAD$", "USD$")
     * @returns {Promise<Object>} Normalized flight search results
     */
    async search(params) {
        // Validate parameters
        this.validateSearchParams(params);

        // Get access token
        const token = await this.authenticate();

        // Build request body
        const requestBody = this.buildRequestBody(params);

        console.log('AmadeusProvider: Searching flights...', {
            origin: params.origin,
            destination: params.destination,
            departureDate: params.departureDate,
            returnDate: params.returnDate,
            adults: params.adults
        });
        console.log('AmadeusProvider: Using access token for search:', token ? `${token.substring(0, 20)}...` : 'undefined');
        console.log('AmadeusProvider: Request body:', JSON.stringify(requestBody, null, 2));

        try {
            const response = await axios.post(this.searchUrl, requestBody, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 45000 // 45 second timeout for search
            });

            console.log('AmadeusProvider: Received response', {
                status: response.status,
                offersCount: response.data?.data?.length || 0
            });

            // Transform Amadeus response to normalized format
            const normalizedResponse = this.transformer.normalizeAmadeusResponse(response.data);

            // Validate the normalized response
            if (!this.transformer.validateNormalizedResponse(normalizedResponse)) {
                throw new Error('Invalid response structure from Amadeus API');
            }

            // Get transformation stats for debugging
            const stats = this.transformer.getTransformationStats(response.data, normalizedResponse);
            console.log('AmadeusProvider: Transformation stats', stats);

            return normalizedResponse;

        } catch (error) {
            console.error('AmadeusProvider: Search failed', error);

            // Log the full error response for debugging
            if (error.response) {
                console.error('AmadeusProvider: Error response status:', error.response.status);
                console.error('AmadeusProvider: Error response headers:', error.response.headers);
                console.error('AmadeusProvider: Error response data:', error.response.data);
                // Log the specific error details
                if (error.response.data && error.response.data.errors) {
                    console.error('AmadeusProvider: Specific errors:', JSON.stringify(error.response.data.errors, null, 2));
                }
            }

            if (error.code === 'ECONNABORTED') {
                throw new Error('Amadeus search request timed out. Please try again.');
            } else if (error.response) {
                const status = error.response.status;
                const data = error.response.data;

                switch (status) {
                    case 400:
                        const errorMsg = data.errors?.[0]?.detail || 'Invalid search parameters';
                        throw new Error(`Amadeus search error: ${errorMsg}`);
                    case 401:
                        // Token might be expired, clear it
                        this.accessToken = null;
                        this.tokenExpiry = null;
                        throw new Error('Amadeus authentication expired. Please try again.');
                    case 403:
                        throw new Error('Access denied to Amadeus service. Check API permissions.');
                    case 429:
                        throw new Error('Too many requests to Amadeus API. Please wait and try again.');
                    case 500:
                    case 502:
                    case 503:
                        throw new Error('Amadeus service is temporarily unavailable');
                    default:
                        throw new Error(`Amadeus API error: ${status} ${data.error || 'Unknown error'}`);
                }
            } else if (error.request) {
                throw new Error('Unable to connect to Amadeus API. Please check your internet connection.');
            } else {
                throw new Error(`Amadeus search error: ${error.message}`);
            }
        }
    }

    /**
     * Build request body for Amadeus API
     * @param {Object} params - Search parameters
     * @returns {Object} Amadeus API request body
     */
    buildRequestBody(params) {
        const { origin, destination, departureDate, returnDate, adults, currency } = params;

        return {
            currencyCode: currency.substring(0, 3), // Extract currency code
            originDestinations: [
                {
                    id: "1",
                    originLocationCode: origin,
                    destinationLocationCode: destination,
                    departureDateTimeRange: {
                        date: departureDate
                    }
                },
                {
                    id: "2",
                    originLocationCode: destination,  // Return leg
                    destinationLocationCode: origin,
                    departureDateTimeRange: {
                        date: returnDate
                    }
                }
            ],
            travelers: Array(adults).fill(null).map((_, i) => ({
                id: String(i + 1),
                travelerType: "ADULT"
            })),
            sources: ["GDS", "NDC"],
            searchCriteria: {
                maxFlightOffers: 250,
                pricingOptions: {
                    fareType: ["PUBLISHED", "NEGOTIATED"],
                    includedCheckedBagsOnly: false,
                    refundableFare: false,
                    noRestrictionFare: false
                },
                flightFilters: {
                    cabinRestrictions: [{
                        cabin: "ECONOMY",
                        coverage: "MOST_SEGMENTS",
                        originDestinationIds: ["1", "2"]
                    }],
                    connectionRestriction: {
                        maxNumberOfConnections: 2,
                        airportChangeAllowed: true,
                        technicalStopsAllowed: true
                    }
                },
                additionalInformation: {
                    chargeableCheckedBags: false,
                    brandedFares: false
                }
            }
        };
    }

    /**
     * Validate search parameters
     * @param {Object} params - Parameters to validate
     */
    validateSearchParams(params) {
        const { origin, destination, departureDate, returnDate, adults, currency } = params;

        if (!origin || origin.length !== 3 || !/^[A-Z]{3}$/.test(origin)) {
            throw new Error('Invalid origin airport code. Must be 3-letter IATA code (uppercase).');
        }

        if (!destination || destination.length !== 3 || !/^[A-Z]{3}$/.test(destination)) {
            throw new Error('Invalid destination airport code. Must be 3-letter IATA code (uppercase).');
        }

        if (!departureDate || !/^\d{4}-\d{2}-\d{2}$/.test(departureDate)) {
            throw new Error('Invalid departure date. Must be in YYYY-MM-DD format.');
        }

        if (!returnDate || !/^\d{4}-\d{2}-\d{2}$/.test(returnDate)) {
            throw new Error('Invalid return date. Must be in YYYY-MM-DD format.');
        }

        if (!adults || adults < 1 || adults > 9) {
            throw new Error('Invalid number of adults. Must be between 1 and 9.');
        }

        if (!currency || currency.length < 3) {
            throw new Error('Invalid currency code.');
        }

        // Validate dates
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const depDate = new Date(departureDate);
        const retDate = new Date(returnDate);

        if (depDate < today) {
            throw new Error('Departure date cannot be in the past.');
        }

        if (retDate < depDate) {
            throw new Error('Return date must be after departure date.');
        }

        // Check if dates are too far in the future (Amadeus typically allows up to 365 days)
        const maxDate = new Date(today);
        maxDate.setDate(maxDate.getDate() + 365);

        if (depDate > maxDate || retDate > maxDate) {
            throw new Error('Search dates cannot be more than 365 days in the future.');
        }
    }

    /**
     * Get provider information
     * @returns {Object} Provider details
     */
    getInfo() {
        return {
            name: 'Amadeus',
            version: '2.9.1',
            description: 'Alternative flight search provider (experimental)',
            baseUrl: this.baseUrl,
            features: [
                'Round-trip flights',
                'Multiple travel classes',
                'Up to 9 passengers',
                'Global coverage',
                'Real-time pricing'
            ]
        };
    }

    /**
     * Test the connection to Amadeus API
     * @returns {Promise<boolean>} True if connection is successful
     */
    async testConnection() {
        try {
            await this.authenticate();
            console.log('AmadeusProvider: Connection test successful');
            return true;
        } catch (error) {
            console.error('AmadeusProvider: Connection test failed', error);
            return false;
        }
    }

    /**
     * Clear authentication token (useful for testing or error recovery)
     */
    clearAuth() {
        this.accessToken = null;
        this.tokenExpiry = null;
        console.log('AmadeusProvider: Authentication token cleared');
    }
}